/**
 * EntityManager — ponte entre as coordenadas da "rede" e o mundo 3D.
 *
 * Recebe snapshots do PlayerService (posições autoritativas em um tick fixo)
 * e interpola suavemente entre eles a cada frame, de modo que carros e
 * pessoas se movam fluidos mesmo com atualizações esparsas.
 *
 * Renderização via Fleet (InstancedMesh): cada entidade ocupa um "slot" de
 * instância em vez de carregar seus próprios meshes — isso colapsa centenas de
 * draw calls em lotes por peça. As etiquetas (sprites de texto) ficam num grupo leve por
 * entidade; cada uma tem o SEU canvas dinâmico, pois mostra "Nome: mensagem"
 * com digitação caractere a caractere quando o backend manda uma reação.
 */
import * as THREE from 'three';
import { probes, P } from '../perfProbes.js';
import { Fleet } from './Fleet.js';
import { advanceWheelSpin, MAX_WHEEL_STEER as MAX_STEER } from './wheelMotion.js';
import { STRIDE_HALF } from './person/pose.js';
import { BlobShadows } from './BlobShadows.js';
import { makeDynamicLabel, drawLabel, resetDynamicLabel, measureLabel, labelText, clipChars, MSG_MAX } from './makeLabel.js';
import { LabelLines } from './LabelLines.js';
import { LabelAtlas, fitsAtlasCell } from './LabelAtlas.js';
import { LabelStacker } from './labelStack.js';
import { randomAppearance } from './personModel.js';
import { randomCarAppearance, normalizeCarAppearance } from './carModel.js';
import { TransformFX } from './TransformFX.js';
import { ConfettiFX } from './ConfettiFX.js';

const CAR_SCALE = 1.25;
const PERSON_SCALE = 1.2;
// fallback da direção horizontal em que a sombra cai — sobrescrito a cada frame
// pela direção REAL do sol (passada em update), p/ bater com a sombra dos prédios.
const SHADOW_DX = 0.81, SHADOW_DZ = 0.59;
const SPAWN_BUDGET = 8; // nº máx. de entidades criadas por frame (suaviza o pico)
const TF_DUR = 0.7;   // s — duração do giro de transformação carro↔pessoa
const TF_TURNS = 2;   // voltas do giro
const JUMP_H = 2.6;   // altura do "salto" ao virar pessoa (cai e amortece)
const CAR_WHEELBASE = 3.0; // entre-eixos (frente−traseira) p/ estimar o ângulo de esterço
// emoji padrão por resultado da ação (quando o backend não manda um emote próprio)
const OUTCOME_EMOJI = { success: '✅', fail: '⚠️', pending: '⏳' };
const LABEL_COLOR = '#eafff2';
const TYPE_MS = 45;   // ms por caractere na digitação (entrada)
const HOLD_MS = 3000; // ms que a mensagem fica 100% exibida DEPOIS de terminar de digitar
//                       (o tempo da digitação NÃO conta para esses 3s)
const ERASE_MS = 28;  // ms por caractere ao APAGAR (saída) — um pouco mais rápido
// FREQUENCIAS PROPRIAS, em TEMPO e nao em contagem de quadros. Contar quadros
// amarra a taxa ao fps: `a cada 4 quadros` vira 15 Hz a 60 fps e 5 Hz a 20 fps —
// justamente ao contrario do desejado (quanto pior o fps, mais o de-clutter
// atrasa). Com acumulador de tempo a taxa e a mesma em qualquer fps.
//
// O que NAO e limitado: a posicao do rotulo e a animacao da elevacao seguem a
// cada quadro. Medido com 500 jogadores, as duas juntas custam 0,032 ms por
// quadro — limita-las economizaria centesimos de milissegundo e faria o rotulo
// andar aos saltos atras do avatar, que se move a 60 Hz.
export const DECLUTTER_HZ = 8;       // recalculo dos ALVOS de elevacao (exportado p/ os harnesses medirem o valor REAL)
const DECLUTTER_DT = 1 / DECLUTTER_HZ;
const MSG_REDRAW_HZ = 15;            // repintura da textura durante a digitacao
const MSG_REDRAW_DT = 1 / MSG_REDRAW_HZ;
// TODOS os rótulos na tela participam do de-clutter — não há mais teto. Havia um
// corte nos 140 mais próximos da câmera, herdado de quando a resolução de colisão
// era todos-contra-todos e escalava ao cubo. Com a grade espacial (labelStack) o
// custo virou linear e medido: 500 rótulos aglomerados custam 1,34 ms por ciclo,
// que a 8 Hz dá 0,36 ms por quadro a 30 fps. O teto excluía os mais distantes do
// empilhamento — justamente os que mais se sobrepõem na vista aérea.
const DECL_CAP = 2048;               // capacidade do resolvedor (= CAP do Fleet)
// nº de ciclos consecutivos de folga antes de RELAXAR (descer) uma label elevada —
// subir p/ resolver colisão é imediato; descer é preguiçoso (anti sobe-e-desce).
const RELAX_PASSES = 4;
// TETO dos pools de reúso (entidade e rótulo). Dimensionado bem acima do pico de
// uma cidade (~200 avatares por tela) para que, em regime, entrar/sair de jogador
// não aloque NADA; acima do teto o objeto é descartado normalmente, então um pico
// atípico não fica preso na memória para sempre.
const POOL_MAX = 512;
// Devolução do excedente do pool de sprites (ver _podarSprites): a cada 10 s devolve
// um quarto da folga nunca usada, nunca abaixo de FOLGA ociosos. Depois de uma rajada
// de fala o pool volta ao tamanho de regime em poucos minutos, sem realocar no caminho.
const PODA_SPRITE_MS = 10000;
const PODA_SPRITE_FRACAO = 0.25;
const SPRITE_FOLGA = 16;
// RESERVA DE TOPO da tela (px) para a pilha de rótulos — o MESMO valor de T em
// SceneManager._usefulArea (o toast de conquistas ocupa ~96px). Está repetido aqui
// porque o EntityManager recebe só a cena e o overlay, nunca o SceneManager; e um
// rótulo que sobe ATRÁS do toast é tão ilegível quanto um que sai pelo topo. Mudou
// a reserva lá, muda aqui.
const SAFE_TOP_PX = 100;
// Folga para DESOCULTAR. Sem ela, um rótulo parado na fronteira do teto piscaria a
// DECLUTTER_HZ (o mesmo motivo de BAND e de RELAX_PASSES logo acima).
const HIDE_HYST_PX = 24;
// Parâmetros de animação passados ao Fleet, REAPROVEITADOS. Eram objetos literais
// criados por avatar por quadro (500 objetos/quadro = ~55 GB pela nursery em 8 h de
// telão). Medido, o ganho de tempo é pequeno — o coletor jovem copia o que está
// VIVO, e estes já nascem mortos —, mas é grátis e correto. Seguro porque
// `Fleet.write` lê tudo em variáveis locais e NÃO guarda a referência.
const _animPerson = { swing: 0, scaleMul: 1, squashY: 1, waveZ: 0, waveZL: 0, phase:0, gait:0, sit:0, settle:0 };
const _animCar = { wheelSpin: 0, steer: 0, scaleMul: 1 };


export class EntityManager {
  constructor(scene, tickMs, renderer, overlay) {
    this.scene = scene;
    this.fleet = new Fleet(scene);
    this.blobs = new BlobShadows(scene); // sombras de contato macias dos avatares

    // Grupo das etiquetas. Vai para a CENA DE SOBREPOSIÇÃO quando o chamador a
    // fornece (ver SceneManager.overlay): assim os rótulos são desenhados DEPOIS
    // do pós-processamento e não passam por bloom, focus blur nem SMAA — ficam
    // chapados e nítidos, com as cores exatas em que foram desenhados. Sem
    // `overlay` (testes headless) caem na cena principal, como antes.
    this.labelRoot = new THREE.Group();
    this.labelRoot.name = 'labels';
    (overlay || scene).add(this.labelRoot);

    this.entities = new Map();
    this._pending = new Map(); // spawns enfileirados (amortização)
    this._fxPending = new Map(); // emote/say chegados antes do avatar existir
    // POOLS de reúso (ver _spawn/_despawn). Entrar/sair de jogador é a operação
    // mais frequente numa sessão longa; sem pool, cada uma alocava um objeto de
    // ~30 campos E criava/destruía uma textura de GPU (o rótulo).
    this._entPool = [];   // objetos-entidade reciclados
    this._labelPool = []; // { lab } sprites individuais reciclados (textura viva)
    this._minOcioso = Infinity; // menor ociosidade do pool na janela (ver _podarSprites)
    this._podaSpriteEm = 0;
    this._seen = new Set();   // reusado por onSnapshot (sem alocar por snapshot)
    this._focus = { minX: 0, maxX: 0, minZ: 0, maxZ: 0, cx: 0, cz: 0, count: 0 }; // cameraFocus
    this.snapDuration = (tickMs ?? 400) / 1000;
    this._prio = 0; // prioridade estável das etiquetas (ordem de criação)
    this._declAcc = DECLUTTER_DT; // acumulador do de-clutter (1o ciclo já no 1o quadro)
    this._stacker = new LabelStacker(DECL_CAP); // empilhamento com grade espacial
    // listas/pool REUTILIZADOS pelo de-clutter (sem alocar por frame)
    this._declPool = []; // objetos-item reaproveitados (cresce até o pico)
    this._declItems = []; // lista de trabalho (referências ao pool)

    // pool de "furacãozinhos" de fumaça p/ a transformação carro↔pessoa
    this._fxGroup = new THREE.Group();
    this._fxGroup.name = 'transformFX';
    scene.add(this._fxGroup);
    this._fxFree = [];
    for (let i = 0; i < 12; i++) {
      const fx = new TransformFX();
      this._fxGroup.add(fx.group);
      this._fxFree.push(fx);
    }

    // pool de confetes p/ a celebração de conquista (1 InstancedMesh/burst = 1 draw call).
    // Bursts vivem desacoplados do avatar (lista _confActive), completando ~3s mesmo se
    // o jogador andar/despawnar.
    this._confGroup = new THREE.Group();
    this._confGroup.name = 'confettiFX';
    scene.add(this._confGroup);
    this._confFree = [];
    this._confActive = [];
    for (let i = 0; i < 24; i++) {
      const c = new ConfettiFX();
      this._confGroup.add(c.group);
      this._confFree.push(c);
    }

    // Config das etiquetas (ajustável no settings)
    this.labelCfg = { fontScale: 1, distance: 6.0, threshold: 8.0 };
    // linhas conectoras de TODOS os jogadores em 1 draw call (antes: 1 por jogador)
    this.lines = new LabelLines(this.labelRoot);
    // NOMES de todos os jogadores em ~1 draw call por página de atlas (antes: 1 por
    // jogador). Só quem não cabe numa célula — mensagem em digitação, nome muito
    // longo — cai no sprite individual do pool. Ver LabelAtlas.
    this.atlas = new LabelAtlas(this.labelRoot, renderer);
    this._labelsVisible = true;
  }

  get count() {
    return this.entities.size;
  }

  /**
   * Foco da câmera dinâmica: retângulo que contém TODOS os jogadores (para
   * garantir que todos fiquem na tela) + seu centro. null se não há ninguém.
   */
  cameraFocus() {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, n = 0;
    for (const e of this.entities.values()) {
      const x = e.current.x, z = e.current.z;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
      n++;
    }
    if (!n) return null;
    const f = this._focus; // objeto reaproveitado (chamado todo frame)
    f.minX = minX; f.maxX = maxX; f.minZ = minZ; f.maxZ = maxZ;
    f.cx = (minX + maxX) / 2; f.cz = (minZ + maxZ) / 2; f.count = n;
    return f;
  }

  /**
   * Alvo da câmera de perseguição ("visão do jogador"): posição/rumo/tipo atuais do
   * avatar `id`, ou null se ele não está em cena. Usa `current` (interpolado por frame),
   * então a câmera segue suave. O `heading` é o rumo de DESLOCAMENTO (rede), estável
   * quando o avatar para — não o giro "virar p/ a câmera" nem o spin de transformação.
   */
  getFollowTarget(id) {
    const e = this.entities.get(id);
    if (!e) return null;
    return { x: e.current.x, z: e.current.z, heading: e.current.heading, kind: e.kind, name: e.name };
  }

  /** Primeiro avatar em cena (conveniência p/ testar a câmera no modo simulado:
   *  /me?sim&follow=first). null se a cidade está vazia. */
  firstId() {
    for (const id of this.entities.keys()) return id;
    return null;
  }

  /** Recebe um snapshot do PlayerService. */
  onSnapshot(snapshot) {
    const now = performance.now() / 1000;
    // JANELA DE INTERPOLAÇÃO MEDIDA. Quem produz os snapshots é um `setInterval`, que só
    // pode ATRASAR — nunca adianta. Com a janela fixa no valor NOMINAL, cada milissegundo
    // de atraso era um milissegundo com todos os avatares congelados no alvo (o `t` satura
    // em 1): medido, 3,7% de cada tick, sem compensação possível e piorando conforme a
    // cidade enche e o quadro fica mais pesado. O produtor agora manda o intervalo real.
    if (snapshot.janelaMs > 0) this.snapDuration = snapshot.janelaMs / 1000;
    const seen = this._seen; // Set reaproveitado (um snapshot a cada ~400ms)
    seen.clear();
    for (const p of snapshot.players) {
      seen.add(p.id);
      const e = this.entities.get(p.id);
      if (e) {
        // prev/target atualizados NO LUGAR (antes eram 2 objetos novos por
        // jogador por snapshot — ~500 objetos/s com 200 jogadores)
        e.prev.x = e.current.x; e.prev.z = e.current.z; e.prev.heading = e.current.heading;
        e.target.x = p.x; e.target.z = p.z; e.target.heading = p.heading;
        e.speed = p.speed ?? 6;
        e.state = p.state;
        e.action = p.action || null; // postura/intent/outcome ao estacionar
        // novo resultado de ação sem mensagem própria → emoji padrão na label (uma vez)
        if (e.action && e.action.outcome && e.action.outcome !== e._lastOutcome) {
          e._lastOutcome = e.action.outcome;
          if (!e._msg && OUTCOME_EMOJI[e.action.outcome]) this._showMsg(e, OUTCOME_EMOJI[e.action.outcome]);
        }
        if (!e.action) e._lastOutcome = null;
        e.t0 = now;
        // trocou de tipo (carro↔pessoa)? inicia o giro de transformação
        const newKind = p.type === 'person' ? 'person' : 'car';
        if (newKind !== e.kind && !e._tf) e._tf = { t: 0, to: newKind, swapped: false };
      } else {
        // ainda não criado: enfileira (ou atualiza a posição pendente)
        this._pending.set(p.id, p);
      }
    }
    // remove quem sumiu do snapshot (ex.: população reduzida)
    for (const [id, e] of this.entities) {
      if (!seen.has(id)) {
        this._despawn(e);
        this.entities.delete(id);
      }
    }
    for (const id of this._pending.keys()) {
      if (!seen.has(id)) { this._pending.delete(id); this._fxPending.delete(id); }
    }
  }

  _spawn(p) {
    const kind = p.type === 'person' ? 'person' : 'car';
    // o jogador tem AMBOS os avatares; renderiza o do tipo atual e guarda os dois
    // p/ a transformação (carro↔pessoa) ao longo do deslocamento.
    const carApp = normalizeCarAppearance(p.carAppearance, p); // corrige modelos antigos do backend
    const personApp = p.personAppearance || randomAppearance(p);
    const appearance = kind === 'car' ? carApp : personApp;
    const colors = appearance.colors;

    // POOL: reaproveita um objeto-entidade aposentado (ou cria o primeiro).
    // _resetEntity reescreve TODOS os campos — nunca parcialmente — senão estado
    // do jogador anterior vazaria (mensagem fantasma, aceno ao nascer, label presa
    // no alto). Ver a lista completa lá.
    const e = this._entPool.pop() || {};
    this._resetEntity(e, p, kind, carApp, personApp, appearance, colors);

    // capacidade do instancing (CAP por tipo): se cheio, aborta e o caller mantém
    // o jogador pendente p/ tentar no próximo frame — sem corromper o buffer.
    const slot = this.fleet.acquire(kind, colors, e);
    if (slot < 0) { this._recycleEntity(e); return null; }
    e.slot = slot;
    const blobSlot = this.blobs.acquire(e);
    if (blobSlot < 0) { this.fleet.release(kind, slot); this._recycleEntity(e); return null; }
    e.blobSlot = blobSlot;

    // etiqueta: normalmente uma célula do atlas compartilhado; o sprite individual
    // só entra quando o texto não cabe na célula (ver _setLabelText)
    e._labelScale = kind === 'car' ? CAR_SCALE : PERSON_SCALE;
    e.lineSlot = this.lines.acquire(e); // instância da linha conectora (pode ser -1)
    e._labelPrio = this._prio++; // prioridade ESTÁVEL → evita troca/glitch
    this._setLabelText(e, labelText(p.name, null), true);
    this._layoutLabel(e);
    return e;
  }

  /**
   * Reinicializa um objeto-entidade (novo ou vindo do pool) para o jogador `p`.
   *
   * NÃO guarda referência ao objeto `p` do snapshot: ele era mantido em `e.meta`
   * e ninguém lia (grep no repositório inteiro: zero leitores). Com 500 jogadores
   * isso segurava ~96 KB de objetos de rede vivos sem motivo.
   * TODOS os campos são atribuídos aqui, incondicionalmente — é o contrato que
   * torna o reúso seguro. Ao acrescentar um campo novo em qualquer ponto do
   * EntityManager, ele PRECISA aparecer nesta lista.
   */
  _resetEntity(e, p, kind, carApp, personApp, appearance, colors) {
    e.id = p.id;
    e.name = p.name;
    e.kind = kind;
    e.carApp = carApp;
    e.personApp = personApp;
    e.colors = colors;
    e.appearance = appearance; // lido pelo Fleet p/ mostrar as peças certas (cabelo/roupa/pés)
    e._tf = null;   // transformação em andamento
    e._fx = null;   // efeito de fumaça (furacão) durante a transformação
    e.prev = e.prev || { x: 0, z: 0, heading: 0 };
    e.target = e.target || { x: 0, z: 0, heading: 0 };
    e.current = e.current || { x: 0, z: 0, heading: 0 };
    e.prev.x = e.target.x = e.current.x = p.x;
    e.prev.z = e.target.z = e.current.z = p.z;
    e.prev.heading = e.target.heading = e.current.heading = p.heading;
    e.speed = p.speed ?? 6;
    e.state = p.state;
    e.action = p.action || null; // postura/intent/outcome ao estacionar
    e._lastOutcome = null;
    e._msg = null;  // mensagem/emoji em exibição na label: { chars, t, dur }
    e._msgAcc = 0;  // acumulador da repintura da mensagem (MSG_REDRAW_HZ)
    e.t0 = performance.now() / 1000;
    e.phase = Math.random() * Math.PI * 2;
    e.wheelSpin = 0;
    e.swing = 0;
    e.gaitPhase=0; e.gait=0; e.sit=0;
    e.steer = 0;            // esterço suavizado das rodas dianteiras
    e._faceH = p.heading;   // rumo renderizado (vira p/ a câmera quando parado)
    e._waving = false;
    e._waveT = 0;
    e._waveDur = 0;
    e._waveCD = 1.5 + Math.random() * 4; // tempo até o 1º aceno
    e._postKind = null;     // postura atual (detecta troca → re-easa a entrada)
    e._postT = 0;           // tempo na postura atual (easing de entrada)
    e._parkT = 0;           // tempo estacionado (assenta o bob da caminhada)
    e._spinAng = 0;         // ângulo acumulado do rodopio (spin)
    e.slot = -1;
    e.blobSlot = -1;
    e.lineSlot = -1;        // instância da linha conectora (LabelLines)
    e.labelLift = 0;        // elevação atual (animada suavemente)
    e._labelLiftTarget = 0; // elevação alvo (calculada pelo de-clutter)
    e._labelPrio = 0;
    e._declSy = null;       // âncora de ORDEM com histerese (de-clutter)
    e._declRelax = 0;       // ciclos de folga antes de relaxar a elevação
    e._labelSeen = false;   // visto no último de-clutter
    e._labelHidden = false; // elevado para fora da área útil → rótulo E linha ocultos
    e._label = null;        // sprite individual do pool (só quando não cabe no atlas)
    e.labelObj = null;
    e.label = null;
    e.atlasSlot = null;     // célula do atlas de nomes ({ page, cell }) ou null
    e._labelText = null;    // texto já desenhado (evita redesenhar sem mudança)
    e._labelIsName = undefined; // e em que CASA ele estava (atlas vs sprite) — ver _setLabelText
    e.labelW = 0;           // tamanho da PÍLULA em unidades de mundo (de-clutter/linha)
    e.labelH = 0;
    e._lx = p.x;            // posição de mundo do avatar no quadro atual (âncora do rótulo)
    e._ly = 0;
    e._lz = p.z;
    e._labelScale = 1;
  }

  /** Devolve o objeto-entidade ao pool (sem segurar referências para fora). */
  _recycleEntity(e) {
    e.appearance = null;
    e.carApp = null;
    e.personApp = null;
    e.colors = null;
    e._label = null;
    e.labelObj = null;
    e.label = null;
    e.atlasSlot = null;
    if (this._entPool.length < POOL_MAX) this._entPool.push(e);
  }

  /** Remove a entidade: libera o slot de instância e tira a etiqueta da cena. */
  _despawn(e) {
    this._releaseFX(e); // some o furacão se estava transformando
    this._fxPending.delete(e.id);
    this.fleet.release(e.kind, e.slot);
    this.blobs.release(e.blobSlot);
    this.lines.release(e.lineSlot);
    if (e.atlasSlot) { this.atlas.release(e.atlasSlot); e.atlasSlot = null; }
    this._releaseSprite(e);
    this._recycleEntity(e);
  }

  /**
   * Define o TEXTO do rotulo e escolhe onde ele e desenhado.
   *
   * Cabe numa celula do atlas (o caso normal: so o nome) -> vai para o atlas, que
   * junta todos os jogadores numa chamada so. Nao cabe (mensagem em digitacao,
   * nome muito comprido) -> sprite individual, o caminho antigo. A troca entre os
   * dois acontece sozinha conforme o texto cresce e encolhe.
   */
  _setLabelText(e, text, isName) {
    // O TEXTO igual não basta: o mesmo texto pode ter que morar em outro lugar.
    //
    // No último quadro do apagamento o sufixo fica VAZIO, e `labelText` devolve o nome
    // puro — mesmo texto, mas ainda pelo caminho de mensagem, ou seja, no sprite. Logo
    // depois a mensagem é encerrada e o redesenho pede o nome puro pelo caminho de nome:
    // o texto não mudou, este atalho voltava, e o jogador ficava num SPRITE INDIVIDUAL
    // para sempre. Numa cidade de 8 h todo mundo fala em algum momento, então as
    // chamadas de desenho de rótulo subiam de 1 (atlas) para ~1 por jogador e nunca
    // desciam. Comparar também a CASA fecha o caminho de volta.
    if (e._labelText === text && e._labelIsName === isName) return; // nada mudou -> nao redesenha
    e._labelText = text;
    e._labelIsName = isName;
    const m = measureLabel(text);
    // O ATLAS é só para o NOME PURO. Duas razões:
    //   • a textura fica CACHEADA por (texto + cor) e compartilhada entre
    //     homônimos — numa população de 500 jogadores há ~33 primeiros nomes
    //     distintos, então 500 pinturas de pílula viram 33 e o atlas inteiro cabe
    //     numa página só (1 draw call em vez de 3);
    //   • um rótulo com MENSAGEM muda a cada caractere. No atlas ele faria célula
    //     entrar e sair 22 vezes por segundo, sujando o cache e desperdiçando
    //     upload. Esse vai para um sprite individual do pool, onde a repintura é
    //     local e a animação de digitação continua exatamente como era.
    if (isName && fitsAtlasCell(m)) {
      const h = this._useAtlas(e, text, m);
      if (h) {
        const sz = this.atlas.sizeOf(h);
        e.labelW = sz.x;
        e.labelH = sz.y;
        return;
      }
      // atlas cheio -> cai para o sprite (nunca fica sem rotulo)
    }
    this._useSprite(e);
    const b = drawLabel(e.labelObj, text, LABEL_COLOR);
    e.labelW = b.x;
    e.labelH = b.y;
  }

  /** Move o rotulo para o atlas, reusando a textura de quem tem o mesmo nome. */
  _useAtlas(e, text, m) {
    const key = text + '|' + LABEL_COLOR;
    if (e.atlasSlot && e.atlasSlot.key === key) return e.atlasSlot;
    if (e.atlasSlot) { this.atlas.release(e.atlasSlot); e.atlasSlot = null; }
    this._releaseSprite(e);
    e.atlasSlot = this.atlas.acquire(key, m, LABEL_COLOR);
    return e.atlasSlot;
  }

  /** Move o rotulo para um sprite individual (soltando a celula do atlas). */
  _useSprite(e) {
    if (e.label) return;
    if (e.atlasSlot) { this.atlas.release(e.atlasSlot); e.atlasSlot = null; }
    let L = this._labelPool.pop();
    if (L) {
      resetDynamicLabel(L.lab); // limpa o cache de conteudo e reexibe o sprite
    } else {
      const lab = makeDynamicLabel();
      lab.sprite.renderOrder = 999; // acima de nuvens/brilhos e dos predios
      L = { lab };
    }
    e._label = L;
    e.labelObj = L.lab;
    e.label = L.lab.sprite;
    this.labelRoot.add(L.lab.sprite);
  }

  /**
   * Devolve o sprite individual ao pool. Canvas + textura + material continuam
   * vivos: e isso que evita alocar e destruir textura de GPU a cada jogador que
   * entra ou sai (sob rotatividade de 8h de telao, e a causa classica de engasgo
   * em placa integrada). Acima do teto do pool, descarta como antes.
   */
  _releaseSprite(e) {
    if (!e._label) return;
    this.labelRoot.remove(e.label);
    if (this._labelPool.length < POOL_MAX) this._labelPool.push(e._label);
    else { e.labelObj.tex.dispose(); e.labelObj.mat.dispose(); }
    e._label = null;
    e.labelObj = null;
    e.label = null;
  }

  /**
   * O pool de sprites SÓ crescia — até POOL_MAX (512), cada um segurando a textura viva
   * de um rótulo. Sprite individual é o caminho de exceção (nome que não cabe na célula
   * do atlas, ou rótulo com mensagem digitando), então em regime a cidade usa uns poucos;
   * mas as rajadas de fala do evento fazem o pico subir e ele nunca descia — ~25 MB de
   * textura parados na placa integrada pelo resto das 8 h.
   *
   * O sinal é o MENOR número de sprites ociosos na janela: é a folga que a cidade não
   * chegou a precisar nem no pior instante. Durante uma rajada o pool esvazia, esse
   * mínimo vai a zero e nada é podado — só depois que a onda passa a folga aparece. Sai
   * uma FRAÇÃO por janela (nunca abaixo de SPRITE_FOLGA), então a devolução acompanha o
   * tamanho do excesso sem nunca cortar o que a próxima onda vai querer de volta.
   */
  _podarSprites(nowMs) {
    const ocioso = this._labelPool.length;
    if (ocioso < this._minOcioso) this._minOcioso = ocioso;
    if (nowMs - this._podaSpriteEm < PODA_SPRITE_MS) return;
    this._podaSpriteEm = nowMs;
    const sobra = Math.min(this._minOcioso, ocioso) - SPRITE_FOLGA;
    if (sobra > 0) {
      let n = Math.max(1, Math.ceil(sobra * PODA_SPRITE_FRACAO));
      while (n-- > 0) {
        const L = this._labelPool.pop();
        if (!L) break;
        L.lab.tex.dispose();
        L.lab.mat.dispose();
      }
    }
    this._minOcioso = this._labelPool.length; // reinicia a medição da janela
  }

  /** Aplica a config das etiquetas (tamanho da fonte etc.) e reposiciona. */
  _applyLabel(e) {
    this._layoutLabel(e);
  }

  /**
   * Posiciona/dimensiona a etiqueta (altura base + elevacao anti-sobreposicao) e
   * estica a linha que a conecta ao avatar. Chamada a cada frame.
   *
   * Tudo em coordenadas de MUNDO: nao ha mais um Group por jogador (eram 500
   * objetos a mais na cena, com matriz recalculada todo quadro). A conversao e a
   * mesma que o Group fazia -- altura = y do avatar + distancia x escala +
   * elevacao; tamanho = pilula x fontScale x escala do avatar.
   */
  _layoutLabel(e) {
    const { fontScale, distance, threshold } = this.labelCfg;
    const sc = e._labelScale || 1;
    const w = e.labelW * fontScale * sc;
    const h = e.labelH * fontScale * sc;
    const wy = e._ly + distance * sc + (e.labelLift || 0);
    // CORTADO pelo de-clutter (a pilha passaria da área útil da tela): a pílula sai
    // com tamanho ZERO — o quad instanciado degenera e não rasteriza nenhum pixel —
    // e a linha conectora some JUNTO. Ocultar só a pílula deixaria exatamente o que
    // motivou o corte: um feixe de linhas subindo e saindo pelo topo do telão.
    // A instância vai para a ORIGEM, não para a posição do avatar: assim os valores
    // param de mudar e o `write` (que pula escritas idênticas) deixa de sujar a faixa
    // de upload — rótulo oculto de avatar em movimento passa a custar ZERO por quadro.
    if (e._labelHidden) {
      if (e.atlasSlot) this.atlas.write(e.atlasSlot, 0, 0, 0, 0, 0);
      else if (e.label) e.label.visible = false;
      if (e.lineSlot >= 0) this.lines.hide(e.lineSlot);
      return;
    }
    if (e.atlasSlot) {
      this.atlas.write(e.atlasSlot, e._lx, wy, e._lz, w, h);
    } else if (e.label) {
      e.label.visible = true;
      e.label.position.set(e._lx, wy, e._lz);
      e.label.scale.set(w, h, 1);
    }
    if (e.lineSlot >= 0) {
      const bottom = 1.5; // logo acima do avatar
      const liftLocal = (e.labelLift || 0) / sc; // mundo->local
      const halfLabel = e.labelH * fontScale * 0.5;
      const top = distance + liftLocal - halfLabel; // logo abaixo da etiqueta
      // mostra a linha quando a etiqueta esta distante OU foi elevada p/ nao colidir
      const show = (distance + liftLocal >= threshold || e.labelLift > 0.4) && top > bottom + 0.3;
      if (show) this.lines.write(e.lineSlot, e._lx, e._ly, e._lz, sc, bottom, top);
      else this.lines.hide(e.lineSlot);
    }
  }

  /** Atualiza a config das etiquetas e reaplica em todas as entidades. */
  setLabelConfig(partial) {
    Object.assign(this.labelCfg, partial);
    for (const e of this.entities.values()) this._applyLabel(e);
  }

  /** Mostra/oculta TODAS as etiquetas (nomes + linhas) de uma vez. Chave-mestra:
   *  o grupo pai fica invisível, então nada renderiza independentemente da lógica
   *  de culling por distância/de-clutter (que segue mexendo em e.label.visible). */
  setLabelsVisible(on) {
    const was = this._labelsVisible;
    this._labelsVisible = !!on;
    this.labelRoot.visible = this._labelsVisible;
    // Enquanto DESLIGADAS, nada é recalculado nem repintado (de-clutter, layout,
    // linhas e repintura de mensagem ficam todos fora). O relógio das mensagens
    // continua correndo, então o texto pode ter ficado para trás: ao RELIGAR,
    // reconcilia uma vez cada rótulo (quem não mudou não redesenha nada).
    if (this._labelsVisible && !was) {
      for (const e of this.entities.values()) this._redrawLabel(e);
    }
  }

  /** Atualiza posições/animações. Chamado a cada frame. `sunDir` = direção
   *  horizontal em que as sombras caem (do SceneManager), para alinhar os blobs. */
  update(dt, camera, sunDir) {
    const now = performance.now() / 1000;
    const nowMs = now * 1000; // relógio dos efeitos transitórios (emote/say)
    const sdx = sunDir ? sunDir.x : SHADOW_DX;
    const sdz = sunDir ? sunDir.z : SHADOW_DZ;
    const slen = sunDir && sunDir.len ? sunDir.len : 1; // comprimento relativo da sombra
    const sunHeading = Math.atan2(sdx, sdz); // a sombra se ALONGA no sentido do sol

    // cria os spawns pendentes em lotes pequenos (evita engasgo ao escalar)
    if (this._pending.size) {
      let budget = SPAWN_BUDGET;
      for (const [id, p] of this._pending) {
        if (budget-- <= 0) break;
        const e = this._spawn(p);
        if (!e) continue; // instancing cheio → mantém pendente, tenta no próximo frame
        this.entities.set(id, e);
        this._pending.delete(id);
        this._flushPendingFx(id); // emote/say que chegaram antes do avatar existir
      }
    }

    // expira reações pendentes cujo avatar não materializou (não vaza sob churn)
    if (this._fxPending.size) {
      for (const [id, fx] of this._fxPending) if (nowMs > (fx._expire || 0)) this._fxPending.delete(id);
    }

    this._podarSprites(nowMs);

    for (const e of this.entities.values()) {
      // mensagem/emoji na label: DIGITA caractere a caractere → fica 100% exibida
      // por ~3s (a digitação não conta) → APAGA caractere a caractere e some.
      // O RELOGIO da mensagem corre todo quadro (a duração precisa ser exata),
      // mas a REPINTURA da textura é limitada a MSG_REDRAW_HZ. Repintar custa
      // canvas 2D + reenvio de textura, e a 45 ms por caractere dava ~22 repinturas
      // por segundo por jogador falando; a 15 Hz o texto avança em blocos de 1–2
      // caracteres, imperceptível na digitação. Com os nomes DESLIGADOS não há o
      // que pintar — só o relógio corre (o texto é reconciliado ao religar).
      if (e._msg) {
        e._msg.t += dt * 1000;
        const ended = e._msg.t >= e._msg.typeDur + e._msg.hold + e._msg.eraseDur;
        if (ended) e._msg = null;
        e._msgAcc += dt;
        if (this._labelsVisible && (ended || e._msgAcc >= MSG_REDRAW_DT)) {
          e._msgAcc = 0;
          this._redrawLabel(e);
        }
      }

      const t = clamp((now - e.t0) / this.snapDuration, 0, 1);
      const x = lerp(e.prev.x, e.target.x, t);
      const z = lerp(e.prev.z, e.target.z, t);
      const heading = lerpAngle(e.prev.heading, e.target.heading, t);
      const travelX = x - e.current.x, travelZ = z - e.current.z;
      const travelHeading = lerpAngle(e.current.heading, heading, 0.5);
      const transforming = !!e._tf;
      e.current.x = x;
      e.current.z = z;
      e.current.heading = heading;

      // transformação carro↔pessoa: gira + "encolhe" (squash) e troca o avatar no
      // ponto mais encolhido (escondendo a troca) — efeito tipo The Sims.
      let spin = 0, scaleMul = 1, landY = 0, squashY = 1;
      if (e._tf) {
        e._tf.t += dt / TF_DUR;
        const tt = Math.min(1, e._tf.t);
        spin = tt * TF_TURNS * Math.PI * 2;
        if (tt >= 0.5 && !e._tf.swapped) { this._swapKind(e, e._tf.to); e._tf.swapped = true; }
        if (tt < 0.5) {
          scaleMul = 0.06 + 0.94 * (1 - Math.sin(tt * Math.PI)); // avatar antigo encolhe
        } else if (e._tf.to === 'person') {
          // POUSO: cresce, SALTA do furacão e CAI, dobrando os joelhos (squash) ao tocar o chão
          const u = (tt - 0.5) / 0.5; // 0..1
          scaleMul = Math.min(1, 0.25 + u * 2.4); // tamanho cheio bem cedo
          const FALL = 0.62;
          if (u < FALL) {
            const f = u / FALL;
            landY = JUMP_H * (1 - f * f); // queda acelerada (parábola)
          } else {
            const v = (u - FALL) / (1 - FALL); // 0..1 após o pouso
            squashY = 1 - 0.42 * Math.sin(Math.min(1, v * 1.35) * Math.PI); // amortece e volta
          }
        } else {
          scaleMul = 0.06 + 0.94 * (1 - Math.sin(tt * Math.PI)); // vira carro: cresce de volta
        }
        if (!e._fx) e._fx = this._fxFree.pop() || null; // furacãozinho de fumaça
        if (e._fx) e._fx.update(dt, tt, x, z);
        if (e._tf.t >= 1) { e._tf = null; spin = 0; scaleMul = 1; landY = 0; squashY = 1; this._releaseFX(e); }
      }

      let y = 0;
      if (e.kind === 'person') {
        let renderHeading = heading;
        let waveZ = 0;  // ergue o braço DIREITO (rot. Z)
        let waveZL = 0; // ergue o braço ESQUERDO (espelhado) — posturas com os 2 braços
        let postureY = 0; // altura dos pulos; posturas apoiadas usam a flexão do rig
        let settle = 0;
        let sitTarget=0;
        const moving=!transforming&&Math.hypot(travelX,travelZ)>1e-5;
        e.gait+=( (moving?1:0)-e.gait )*(1-Math.exp(-dt*14));
        if(e.gait<1e-4)e.gait=0;
        if(e.gait>1-1e-4)e.gait=1;
        if(moving)e.gaitPhase=(e.gaitPhase+Math.hypot(travelX,travelZ)/(this.fleet.person.scale*4*STRIDE_HALF)*Math.PI*2)%(Math.PI*2);
        // PARADO em frente ao prédio (aguardando/interação): vira p/ a CÂMERA,
        // relaxa a passada e anima conforme a postura (action.kind).
        if (e.state === 'parked' && !e._tf && camera) {
          const camH = Math.atan2(camera.position.x - x, camera.position.z - z);
          e._faceH = easeAngle(e._faceH, camH, Math.min(1, dt * 3));
          renderHeading = e._faceH;
          // POSTURA conforme a ação que o backend mandou ao estacionar (action.kind).
          // REFINO: toda postura ENTRA com easing (`ease` 0→1 em ~0.4s — sem "pop" ao
          // trocar), a passada assenta com flexão dos joelhos e
          // as curvas têm envelopes orgânicos (rajadas/frases/batidas — não metrônomos).
          const kind = (e.action && e.action.kind) || 'wait';
          if (e._postKind !== kind) { e._postKind = kind; e._postT = 0; e._spinAng = 0; }
          e._postT += dt;
          e._parkT += dt;
          const ease = smooth01(0, 0.4, e._postT);
          let sw = 0; // swing-alvo da postura (misturado com `ease` no fim do bloco)
          if (kind === 'work') {
            // OCUPADO: RAJADAS de atividade (mexe, pausa, volta a mexer) + as MÃOS se
            // ALTERNAM erguendo (pega/posiciona algo ora com uma, ora com outra)
            e.phase += dt * 5.2;
            const act = 0.35 + 0.65 * smooth01(-0.2, 0.6, Math.sin(e.phase * 0.33));
            sw = Math.sin(e.phase) * 0.17 * act;
            waveZ = Math.pow(Math.max(0, Math.sin(e.phase * 0.41)), 3) * 0.85 * ease;
            waveZL = Math.pow(Math.max(0, Math.sin(e.phase * 0.41 + Math.PI)), 3) * 0.85 * ease;
            settle = Math.abs(Math.sin(e.phase)) * 0.012 * act * ease;
            e._waving = false; e._waveCD = 1.5;
          } else if (kind === 'browse') {
            // OLHANDO EM VOLTA: "olhadelas" — fica de frente e dá viradas rápidas com
            // DWELL nas pontas (sin³) + passinho de apoio quando está virando
            e.phase += dt * 1.05;
            const sweep = Math.sin(e.phase);
            renderHeading = e._faceH + sweep * sweep * sweep * 0.62 * ease;
            sw = Math.cos(e.phase) * 0.1;
            settle = Math.abs(Math.cos(e.phase)) * 0.02 * ease;
            e._waving = false; e._waveCD = 1.5;
          } else if (kind === 'talk') {
            // GESTICULANDO em FRASES: pulsos de gesto com pausas (fala ~2s, escuta ~2s)
            // ALTERNANDO as mãos entre frases + leve ênfase do corpo — combina com o `say`
            e.phase += dt * 6;
            const cyc = Math.sin(e.phase * 0.28);
            const phrase = smooth01(0.1, 0.7, cyc);        // frase com a mão direita…
            const phraseL = smooth01(0.1, 0.7, -cyc);      // …depois com a esquerda
            sw = Math.sin(e.phase) * 0.26 * (0.25 + 0.75 * Math.max(phrase, phraseL));
            waveZ = (0.55 + Math.max(0, Math.sin(e.phase * 1.7)) * 0.75) * phrase * ease;
            waveZL = (0.55 + Math.max(0, Math.sin(e.phase * 1.55)) * 0.75) * phraseL * ease;
            renderHeading = e._faceH + Math.sin(e.phase * 0.45) * 0.09 * ease;
          } else if (kind === 'dance') {
            // DANÇANDO: groove em 2 tempos — quica NO tempo, BRAÇOS ALTERNANDO na
            // batida (um sobe, o outro desce) e gingado; squash amortece cada pouso
            e.phase += dt * 6.4;
            const beat = Math.abs(Math.sin(e.phase)); // 0 chão .. 1 alto (2 batidas/ciclo)
            postureY = beat * 0.2 * ease;
            squashY = 1 + ((1 - 0.12 * Math.pow(1 - beat, 2)) - 1) * ease;
            sw = Math.sin(e.phase) * 0.42;
            waveZ = (1.15 + Math.sin(e.phase + Math.PI / 2) * 0.85) * ease;
            waveZL = (1.15 + Math.sin(e.phase - Math.PI / 2) * 0.85) * ease;
            renderHeading = e._faceH + Math.sin(e.phase * 0.5) * 0.65 * ease;
            e._waving = false; e._waveCD = 1.5;
          } else if (kind === 'celebrate') {
            // COMEMORANDO: pula de alegria com os DOIS braços pro alto (bombando com
            // leve defasagem), perninhas vivas no ar e pouso amortecido (squash)
            e.phase += dt * 4.5;
            const j = Math.pow(Math.abs(Math.sin(e.phase)), 0.85); // 0 chão .. 1 topo
            postureY = j * 0.8 * ease;
            waveZ = (1.95 + j * 0.65) * ease;
            waveZL = (1.85 + j * 0.65 + Math.sin(e.phase * 2) * 0.12) * ease;
            sw = Math.sin(e.phase * 2) * (0.1 + j * 0.1);
            squashY = 1 + ((1 - 0.22 * Math.pow(1 - j, 3)) - 1) * ease;
            e._waving = false; e._waveCD = 1.5;
          } else if (kind === 'excited') {
            // ANIMADO: tremor rápido com os DOIS braços vibrando em CONTRAFASE +
            // PULINHOS periódicos (a empolgação "transborda" a cada ~2s)
            e.phase += dt * 14;
            const hop = Math.pow(Math.max(0, Math.sin(e.phase * 0.22)), 2);
            sw = Math.sin(e.phase) * (0.14 + hop * 0.1);
            waveZ = (1.45 + Math.sin(e.phase * 1.27) * 0.55 + hop * 0.5) * ease;
            waveZL = (1.45 + Math.sin(e.phase * 1.27 + Math.PI) * 0.55 + hop * 0.5) * ease;
            postureY = (Math.abs(Math.sin(e.phase * 0.5)) * 0.05 + hop * 0.16) * ease;
            renderHeading = e._faceH + Math.sin(e.phase * 0.9) * 0.12 * ease;
            e._waving = false; e._waveCD = 1.5;
          } else if (kind === 'spin') {
            // RODOPIANDO: WIND-UP (parte do zero) e gira com velocidade VIVA; os DOIS
            // braços ABERTOS sobem com a velocidade (tipo "avião"), bob no passo do giro
            e.phase += dt;
            const spinV = (3.4 + Math.sin(e.phase * 1.6) * 1.0) * ease;
            e._spinAng += spinV * dt;
            renderHeading = e._faceH + e._spinAng;
            sw = (0.3 + Math.sin(e.phase * 5) * 0.08) * ease;
            waveZ = (1.05 + spinV * 0.09) * ease;
            waveZL = (1.05 + spinV * 0.09) * ease;
            settle = Math.abs(Math.sin(e._spinAng * 2)) * 0.025 * ease;
            e._waving = false; e._waveCD = 1.5;
          } else if (kind === 'sit') {
            // SENTADO/AGACHADO: SENTA com easing (não achata seco), respira devagar
            // e ajeita o peso de tempos em tempos
            e.phase += dt * 1.3;
            sitTarget=1;
            sw = Math.sin(e.phase * 0.22) * 0.05;
            settle = (0.5 + 0.5 * Math.sin(e.phase)) * 0.014 * ease;
            e._waving = false; e._waveCD = 1.5;
          } else {
            // wait (padrão): RESPIRA, troca o peso de leve e acena de tempos em tempos
            e.phase += dt;
            sw = 0;
            settle = (0.5 + 0.5 * Math.sin(e.phase * 1.6)) * 0.008 * ease;
            renderHeading = e._faceH + Math.sin(e.phase * 0.35) * 0.04 * ease;
            e._waveCD -= dt;
            if (!e._waving && e._waveCD <= 0) {
              e._waving = true; e._waveT = 0; e._waveDur = 1.6 + Math.random() * 1.4;
            }
            if (e._waving) {
              e._waveT += dt;
              const D = e._waveDur;
              const env = Math.min(smooth01(0, 0.28, e._waveT), 1 - smooth01(D - 0.32, D, e._waveT));
              waveZ = env * (2.3 + Math.sin(e._waveT * 9) * 0.45) * ease; // braço erguido + oscila
              if (e._waveT >= D) { e._waving = false; e._waveCD = 5 + Math.random() * 8; }
            }
          }
          // ENTRADA suave: mistura o swing atual (vindo da caminhada/postura anterior)
          // com o alvo da postura — mata o "pop" de membros ao trocar de animação.
          e.swing = e.swing * (1 - ease) + sw * ease;
        } else {
          e.swing = Math.cos(e.gaitPhase) * .5 * e.gait;
          e._faceH = heading;
          if (e._waving) { e._waving = false; }
          e._waveCD = 1.5 + Math.random() * 4;
          e._postKind = null; // re-estacionar → a postura re-entra com easing
          e._parkT = 0;
        }
        e.sit+=(sitTarget-e.sit)*(1-Math.exp(-dt*8));
        if(e.sit<1e-5)e.sit=0;
        if(e.sit>1-1e-5)e.sit=1;
        y = landY + postureY;
        _animPerson.swing = e.swing;
        _animPerson.scaleMul = scaleMul;
        _animPerson.squashY = squashY;
        _animPerson.waveZ = waveZ;
        _animPerson.waveZL = waveZL;
        _animPerson.phase=e.gaitPhase;_animPerson.gait=e.gait;_animPerson.sit=e.sit;_animPerson.settle=settle;
        this.fleet.write('person', e.slot, x, y, z, renderHeading + spin, _animPerson);
      } else {
        if (!transforming) {
          e.wheelSpin = advanceWheelSpin(e.wheelSpin, travelX, travelZ, travelHeading,
            this.fleet.carWheelRadius(e.slot));
        }
        // ESTERÇO das rodas dianteiras: ângulo ≈ atan(entre-eixos · taxa_de_guinada /
        // velocidade). A taxa vem da variação de rumo por snapshot → curva FECHADA
        // (muito rumo, pouca velocidade) esterca mais; curva ABERTA, menos. Suavizado
        // p/ não tremer; zera parado/transformando.
        let dHead = e.target.heading - e.prev.heading;
        dHead = Math.atan2(Math.sin(dHead), Math.cos(dHead)); // normaliza p/ (−π,π]
        const yawRate = dHead / this.snapDuration;
        const steerTarget = (e._tf || e.speed < 1.5)
          ? 0
          : clamp(Math.atan2(yawRate * CAR_WHEELBASE, Math.max(e.speed, 2.5)), -MAX_STEER, MAX_STEER);
        e.steer = (e.steer || 0) + (steerTarget - (e.steer || 0)) * Math.min(1, dt * 9);
        _animCar.wheelSpin = e.wheelSpin;
        _animCar.steer = e.steer;
        _animCar.scaleMul = scaleMul;
        this.fleet.write('car', e.slot, x, 0, z, heading + spin, _animCar);
      }

      // sombra de contato (blob) que segue o avatar: carro = oval alongada no
      // sentido do veículo; pessoa = quase circular. Encolhe junto na transformação.
      // sombra ANCORADA sob o avatar e PROJETADA no sentido do sol (igual às dos
      // prédios). Orienta-se pelo SOL (não pelo avatar) e alonga com o sol baixo.
      // `so` desloca o CENTRO só o bastante p/ a borda de trás encostar no objeto —
      // assim a base fica embaixo dele e o resto se projeta para o lado oposto ao sol.
      const car = e.kind === 'car';
      const objR = car ? 2.3 : 0.7;
      const wid = (car ? 3.2 : 1.5) * scaleMul;
      const len = (car ? 3.6 + 2.6 * slen : 1.2 + 1.3 * slen) * scaleMul;
      const so = len * 0.5 - objR * scaleMul;
      this.blobs.write(e.blobSlot, x + sdx * so, z + sdz * so, sunHeading, wid, len);

      // ancora do rotulo (nao ha Group por jogador -- ver _layoutLabel)
      e._lx = x; e._ly = y; e._lz = z;
    }

    this.fleet.flush();
    this.blobs.flush();

    // confetes ativos: integra e recicla os que terminaram (contrato: update()→true no fim)
    for (let i = this._confActive.length - 1; i >= 0; i--) {
      if (this._confActive[i].update(dt)) {
        this._confFree.push(this._confActive[i]);
        this._confActive.splice(i, 1);
      }
    }

    // anti-sobreposição das etiquetas (eleva quem colidir, mantendo a linha).
    // Com os nomes DESLIGADOS nada disso é visível → pula o trabalho inteiro
    // (projeção + 2 sorts + resolução de colisão de até 140 rótulos por ciclo).
    if (this._labelsVisible) {
      probes.begin(P.labels); // inclui as escritas de atlas/linhas feitas no layout
      this._declutterLabels(camera, dt);
      for (const e of this.entities.values()) this._layoutLabel(e);
      this.lines.flush();
      this.atlas.flush();
      probes.end(P.labels);
    }
  }

  /** Devolve o efeito de fumaça ao pool (esconde). */
  _releaseFX(e) {
    if (e._fx) { e._fx.hide(); this._fxFree.push(e._fx); e._fx = null; }
  }

  /** Estoura confetes ao redor do avatar `id` (celebração de conquista). Degrada em
   *  silêncio se o pool esgotar; o burst é independente do avatar após disparar. */
  burstConfetti(id) {
    const e = this.entities.get(id);
    if (!e) return;
    const c = this._confFree.pop();
    if (!c) return; // pool esgotado (rajada) → sem burst, sem alocar
    c.play(e.current.x, 1.7, e.current.z);
    this._confActive.push(c);
  }

  /** Troca o avatar renderizado (carro↔pessoa) mantendo a mesma entidade: libera
   *  o slot antigo no Fleet e adquire o novo com a aparência do novo tipo. */
  _swapKind(e, newKind) {
    this.fleet.release(e.kind, e.slot);
    e.kind = newKind;
    e.appearance = newKind === 'car' ? e.carApp : e.personApp;
    e.colors = e.appearance.colors;
    e.slot = this.fleet.acquire(newKind, e.colors, e);
    if (e.slot < 0) {
      // bucket do novo tipo LOTADO (CAP): desfaz a troca — o release acima acabou de
      // abrir uma vaga no bucket antigo, então o rollback não falha. Melhor um avatar
      // que não transforma do que slot -1 envenenando writes/releases futuros.
      e.kind = newKind === 'car' ? 'person' : 'car';
      e.appearance = e.kind === 'car' ? e.carApp : e.personApp;
      e.colors = e.appearance.colors;
      e.slot = this.fleet.acquire(e.kind, e.colors, e);
    }
    e._labelScale = e.kind === 'car' ? CAR_SCALE : PERSON_SCALE;
  }

  /**
   * De-clutter das etiquetas em ESPAÇO DE TELA: projeta cada etiqueta visível,
   * detecta sobreposições e ELEVA as que colidem (mantendo a linha vertical que
   * liga ao avatar), para que todo nome fique legível. Também servirá para as
   * caixas de diálogo futuras (mesmo mecanismo de elevação).
   */
  _declutterLabels(camera, dt) {
    if (!camera) return;
    // THROTTLE por TEMPO (não por contagem de quadros — ver DECLUTTER_HZ): os
    // rótulos se movem devagar na tela, então recalcular os ALVOS 6×/s basta. A
    // ANIMAÇÃO até o alvo (abaixo) roda TODO quadro, então o movimento segue suave.
    this._declAcc += dt;
    if (this._declAcc >= DECLUTTER_DT) {
      this._declAcc = 0;
      this._computeLabelTargets(camera);
    }

    // ANIMA suavemente a elevação até o alvo (transições graduais, sem glitch)
    const s = Math.min(1, dt * 6);
    for (const e of this.entities.values()) {
      const tgt = e._labelLiftTarget || 0;
      e.labelLift += (tgt - e.labelLift) * s;
      if (Math.abs(tgt - e.labelLift) < 0.02) e.labelLift = tgt; // assenta
    }
  }

  /**
   * Calcula os ALVOS de elevação das etiquetas (de-clutter). É a parte CARA —
   * roda a DECLUTTER_HZ (6×/s). REUSA listas/pool (this._decl*) p/ não
   * alocar nada por chamada (zero pressão de GC numa execução de horas).
   */
  _computeLabelTargets(camera) {
    const H = window.innerHeight;
    const W = window.innerWidth;
    const tanHalf = Math.tan((camera.fov * Math.PI) / 180 / 2);
    const k = H / (2 * tanHalf); // px por unidade-mundo a 1 de distância
    // A etiqueta é elevada em +Y do MUNDO, mas a câmera é inclinada (vista aérea):
    // subir Δy no mundo só move Δy·upY na TELA (upY = componente Y do eixo "up" da
    // câmera). Sem compensar, cada elevação fica curta e o ERRO ACUMULA na coluna →
    // o topo volta a sobrepor. Dividir por upY faz a separação na tela bater certo.
    const upY = Math.max(0.35, camera.matrixWorld.elements[5]);
    const v = this._tmpVec || (this._tmpVec = new THREE.Vector3());
    const pool = this._declPool;   // objetos-item reaproveitados (não realoca)
    const items = this._declItems; // lista de trabalho (refs ao pool)
    items.length = 0;

    const { distance, fontScale } = this.labelCfg; // fora do laço: é o mesmo p/ todos
    for (const e of this.entities.values()) e._labelSeen = false;
    let count = 0;
    let maxBaseSy = -Infinity; // rótulo mais BAIXO na tela (base da pilha mais alta)
    for (const e of this.entities.values()) {
      const sc = e._labelScale;
      // âncora ESTÁVEL: altura da etiqueta a partir do CHÃO (y=0), IGNORANDO o y da
      // animação (bob da caminhada/pulos de celebrate). Antes o pulo entrava aqui e
      // a decisão de colisão/ordem oscilava com a animação → labels trocando de lugar.
      const wy = distance * sc;
      v.set(e.current.x, wy, e.current.z);
      const camDist = camera.position.distanceTo(v);
      v.project(camera);
      if (v.z > 1 || v.x < -1.3 || v.x > 1.3 || v.y < -1.3 || v.y > 1.3) continue; // fora da tela
      e._labelSeen = true;
      const it = pool[count] || (pool[count] = {}); // pega/cria do pool (sem lixo)
      it.e = e;
      it.camDist = camDist; // usado na conversão px→mundo da elevação (não mais p/ ordenar)
      it.sx = (v.x * 0.5 + 0.5) * W;
      it.sy = (-v.y * 0.5 + 0.5) * H;
      it.baseSy = it.sy;
      if (it.baseSy > maxBaseSy) maxBaseSy = it.baseSy;
      it.halfH = (e.labelH * fontScale * sc * 0.5) * k / camDist;
      it.halfW = (e.labelW * fontScale * sc * 0.5) * k / camDist;
      items.push(it);
      count++;
    }
    // As sobras do pool (o que o PICO usou e o regime não usa mais) seguem apontando
    // para a entidade daquele frame. Sem soltar, um pico de 700 nomes deixa ~550
    // avatares MORTOS vivos na memória — com malha, materiais e o canvas do rótulo —
    // pelo resto das 8 h, embora já tenham saído da cidade há horas.
    for (let i = count; i < pool.length; i++) if (pool[i].e) pool[i].e = null;
    // ORDEM da pilha: da FRENTE p/ trás (avatar mais EMBAIXO na tela = mais perto da
    // câmera → fica na BASE, etiqueta colada nele; os de TRÁS empilham ACIMA). Antes
    // ordenava por prioridade de SPAWN, então o avatar da frente podia ir parar no TOPO
    // da pilha (etiqueta esticada longe dele — bug reportado). Quem fica na base; quem
    // colide sobe ACIMA das já colocadas (empilha).
    const GAP = 3;
    // TETO DE ELEVAÇÃO = a ÁREA ÚTIL DA TELA, não um múltiplo dela.
    //
    // Avatares numa MESMA coluna de tela só se separam EMPILHANDO na vertical (a
    // linha conectora é vertical, não dá p/ espalhar de lado). O teto era H×1.2 —
    // 1,2 ALTURAS DE TELA acima do avatar — e não havia corte nenhum por sair do
    // quadro. Medido com 200 pessoas no MESMO prédio a 1920×1080: elevação média de
    // 166u (prédio tem 11–20u), e 81% dos nomes projetavam FORA do quadro. O telão
    // mostrava um feixe de linhas conectoras saindo pelo topo, sem nome legível.
    //
    // Agora a pilha para na borda de cima da área útil: ninguém precisa subir mais
    // do que a distância entre a base mais baixa em cena e esse limite. Quem ainda
    // assim não couber é OCULTADO abaixo — poucos nomes legíveis valem mais que
    // muitos ilegíveis, e nenhum vale um nome fora da tela.
    const topLimit = Math.min(SAFE_TOP_PX, H * 0.2); // janela baixa: reserva proporcional
    const MAX_LIFT_PX = Math.max(0, maxBaseSy - topLimit);
    // Mais EMBAIXO na tela (baseSy maior = mais à frente) primeiro → âncora na base.
    // A ordem usa uma posição COM HISTERESE (e._declSy): só re-ancora quando a base
    // andou mais de BAND px na tela. Sem isso, vizinhos na fronteira de uma faixa
    // trocavam de ordem a cada deslize da câmera/animação → labels se alternando
    // (bug reportado). Com a âncora congelada, a ordem só muda em movimento REAL.
    const BAND = 20;
    for (const it of items) {
      const e = it.e;
      if (e._declSy == null || Math.abs(it.baseSy - e._declSy) > BAND) e._declSy = it.baseSy;
      it.stableSy = e._declSy;
    }
    items.sort((a, b) =>
      (Math.round(b.stableSy / BAND) - Math.round(a.stableSy / BAND)) ||
      (a.e._labelPrio - b.e._labelPrio));
    // Empilhamento: só eleva quando há colisão REAL (sobreposição nos DOIS eixos),
    // iterando porque subir acima de uma caixa pode revelar outra acima. A busca
    // usa GRADE ESPACIAL (labelStack) — mesmo resultado da comparação de todos
    // contra todos, com 16× menos comparações no cenário aglomerado. Provado por
    // scripts/stackHarness.mjs contra a implementação de referência.
    this._stacker.resolve(items, items.length, GAP, MAX_LIFT_PX);
    for (const it of items) {
      const liftPx = it.baseSy - it.sy;
      const nt = liftPx > 0.5 ? (liftPx * it.camDist) / (k * upY) : 0; // px → mundo (compensa inclinação)
      const e = it.e;
      // HISTERESE ASSIMÉTRICA: SUBIR (resolver colisão) aplica NA HORA; DESCER
      // (relaxar) só depois de RELAX_PASSES ciclos consecutivos pedindo menos.
      // Colisões borderline que ligam/desligam deixavam o alvo alternando 0↔alto
      // (label subindo e descendo — bug reportado); com o relax preguiçoso a label
      // fica onde está até a folga ser ESTÁVEL.
      if (nt > e._labelLiftTarget + 0.9) {
        e._labelLiftTarget = nt;
        e._declRelax = 0;
      } else if (nt < e._labelLiftTarget - 0.9) {
        if (++e._declRelax >= RELAX_PASSES) { e._labelLiftTarget = nt; e._declRelax = 0; }
      } else {
        e._declRelax = 0;
      }
      // CORTE por área útil: quem foi EMPILHADO acima do limite superior some — a
      // pílula E a linha conectora (ver _layoutLabel). Só quem SUBIU entra no corte:
      // um avatar que já nasce no alto da tela continua exatamente como era; o
      // defeito é o empilhamento jogando o nome para fora, não a posição natural.
      // ORDEM DO SACRIFÍCIO: o empilhamento já põe o avatar mais BAIXO na tela na
      // base da pilha (= o mais perto da câmera) e sobe os de trás; então o topo da
      // pilha — justamente o que estoura — é sempre o mais DISTANTE. A frente fica.
      // A conta parte do alvo JÁ PASSADO PELA HISTERESE (não do `it.sy` recém-resolvido:
      // quando o relax segura um alvo antigo mais alto, é ele que vai para a tela) e
      // PROJETA a posição elevada de verdade. O modelo do empilhamento mede tudo na
      // distância do avatar SEM elevação; nesta câmera aérea um rótulo 80u mais alto
      // está bem mais PERTO, então sai maior e mais acima do que o modelo prevê —
      // medido, até 84 px de diferença, o bastante para o nome parar atrás do toast.
      if (e._labelLiftTarget > 0.01) {
        const sc = e._labelScale;
        v.set(e.current.x, distance * sc + e._labelLiftTarget, e.current.z);
        const d = camera.position.distanceTo(v);
        v.project(camera);
        const top = (-v.y * 0.5 + 0.5) * H - (e.labelH * fontScale * sc * 0.5) * k / d;
        if (top < topLimit) e._labelHidden = true;
        else if (top > topLimit + HIDE_HYST_PX) e._labelHidden = false;
      } else e._labelHidden = false;
    }
    // etiquetas que saíram de cena/colisão → voltam suavemente à base (e reaparecem:
    // sem elevação não há corte, e quem está fora do frustum não custa pixel nenhum)
    for (const e of this.entities.values()) {
      if (!e._labelSeen) { e._labelLiftTarget = 0; e._declSy = null; e._labelHidden = false; }
    }
  }

  /* ------------ reações: mensagem/emoji DENTRO da label ("nome: ...") ------------ */

  /** Mostra um emoji na label do avatar `id` ("nome: 👍"), digitado, por ~2s. */
  emote(id, emoji, durationMs) {
    this._msgTo(id, emoji, durationMs);
  }

  /** Mostra uma mensagem na label do avatar `id` ("nome: texto"), digitada, por ~2s. */
  say(id, text, durationMs) {
    this._msgTo(id, text, durationMs);
  }

  /** Troca o visual do avatar já em cena, REUSANDO o efeito de transformação (giro +
   *  fumaça) mesmo quando o tipo não muda (carro→carro / pessoa→pessoa). Atualiza as
   *  duas aparências guardadas; o novo visual é aplicado no _swapKind, no meio do giro. */
  restyle(id, appearance) {
    const e = this.entities.get(id);
    if (!e || !appearance) return;
    if (appearance.car) e.carApp = normalizeCarAppearance(appearance.car, e);
    if (appearance.person) e.personApp = appearance.person;
    if (!e._tf) e._tf = { t: 0, to: e.kind, swapped: false }; // gira p/ o MESMO tipo → re-adquire slot
  }

  _msgTo(id, text, durationMs) {
    if (text == null || text === '') return;
    const e = this.entities.get(id);
    if (e) this._showMsg(e, text, durationMs);
    else this._stashFx(id, { text, durationMs }); // ainda não spawnou
  }

  /** Inicia "nome: text": digita caractere a caractere e, ao completar, segura
   *  por `durationMs` (padrão ~3s — o tempo da digitação NÃO conta).
   *
   *  O texto é CORTADO aqui, não só na hora de desenhar: a duração é derivada do
   *  número de caracteres (45 ms digitando + 28 ms apagando cada um), então uma
   *  colagem de 2.000 caracteres ficava 149 s na tela — e os caracteres além do que
   *  cabe na pílula nunca chegariam a aparecer (ver MAX_LINES em makeLabel). Com o
   *  teto, a mensagem mais longa possível dura ~8 s. */
  _showMsg(e, text, durationMs) {
    const chars = [...clipChars(text, MSG_MAX)];
    e._msg = {
      chars, t: 0,
      typeDur: chars.length * TYPE_MS,
      hold: durationMs || HOLD_MS,
      eraseDur: chars.length * ERASE_MS
    };
    this._redrawLabel(e);
  }

  /** Redesenha a label conforme a fase: digitando → segurando → apagando. */
  _redrawLabel(e) {
    let suffix = null;
    if (e._msg) {
      const { chars, t, typeDur, hold } = e._msg;
      const len = chars.length;
      let n;
      if (t < typeDur) n = Math.floor(t / TYPE_MS);                 // digitando (entrada)
      else if (t < typeDur + hold) n = len;                          // 100% exibida (~3s)
      else n = len - Math.floor((t - typeDur - hold) / ERASE_MS);    // apagando (saída)
      n = Math.min(len, Math.max(0, n));
      suffix = chars.slice(0, n).join('');
    }
    // sufixo VAZIO (começo da digitação / fim do apagamento) é nome puro para todo
    // efeito: o texto é só o nome, então o lugar dele é o atlas.
    this._setLabelText(e, labelText(e.name, suffix), suffix == null || suffix === '');
  }

  /** Guarda a mensagem chegada antes do avatar existir (aplicada no _spawn). */
  _stashFx(id, data) {
    data._expire = performance.now() + 8000; // TTL anti-vazamento (avatar não materializou)
    this._fxPending.set(id, data);
  }

  _flushPendingFx(id) {
    const fx = this._fxPending.get(id);
    if (!fx) return;
    this._fxPending.delete(id);
    const e = this.entities.get(id);
    if (e) this._showMsg(e, fx.text, fx.durationMs);
  }

  /** Preenche `out` com as posições atuais das PESSOAS [x,z,x,z,...] (buffer
   *  reaproveitado, sem alocar). Usado pela interação dos arbustos. */
  collectPeople(out) {
    out.length = 0;
    for (const e of this.entities.values()) {
      if (e.kind === 'person') { out.push(e.current.x, e.current.z); }
    }
    return out;
  }
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/** Interpola ângulos pelo caminho mais curto.
 *  Reduz por RESTO, não num laço: `a` vem de um rumo que enrola (o avatar dá voltas na
 *  cidade a tarde inteira), e o laço custaria uma iteração por volta acumulada, todo
 *  quadro, para cada avatar em cena. Ver a mesma correção em SceneManager.shortAngle. */
function lerpAngle(a, b, t) {
  const TAU = Math.PI * 2;
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  return a + d * t;
}
// gira `a` em direção a `b` pelo caminho curto, por uma fração `t` (suaviza o
// "virar para a câmera" quando o avatar estaciona).
const easeAngle = lerpAngle;

function smooth01(a, b, x) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
